import axios from "axios";
import { compact } from "lodash-es";
import { useContext } from "react";
import { QueryKey, useMutation, useQuery } from "react-query";
import { useClient } from "urql";

import { KycContext } from "../contexts/kyc/KycContext";
import {
    IKycContextApplicationFragment,
    IKycContextQuery,
    IKycContextQueryVariables,
    KycContextDocument,
} from "../contexts/kyc/KycContext.generated";
import { useGQLKeyInvalidator } from "../hooks/useGQLKeyInvalidator";
import { NO_AUTH_HEADERS_INIT } from "../services/auth";
import { AccountPrefill } from "../types/accounts";
import { BankDetails, IdentifierType } from "../types/bankDetails";
import { UnitApplicationRequest } from "../types/unit";
import { catchAxiosResponseError } from "../utils/catchAxiosResponseError";
import { FlagsByTypename } from "../utils/urql/getAdditionalTypenamesForOperation";
import { getOperationContext } from "../utils/urql/getOperationContext";
import { withoutUndefinedValues } from "../utils/withoutUndefinedValues";
import { QueryOptions } from "./defaults";
import { fetchWithAuth, ResponseError } from "./fetchWithAuth";

export const AccountsKey = {
    accountPrefill: (token: string): QueryKey => {
        return ["accountPrefill", { token }];
    },
    bankDetails: (identifierType: IdentifierType, identifierValue: string): QueryKey => {
        return ["bankDetails", { identifierType, identifierValue }];
    },
    promoCode: (promoCode: string): QueryKey => {
        return ["promoCode", promoCode];
    },
};

interface IBlueprintsApplicationResponse {
    app_id: string;
    // other data omitted...
}

const KYC_CONTEXT_ADDITIONAL_TYPENAME_FLAGS: FlagsByTypename<IKycContextQuery> = {
    ApplicationDocument: true,
    DefaultError: false,
};
export function useApplyForUserMutation() {
    const urqlClient = useClient();
    const invalidateApplications = useGQLKeyInvalidator("ApplicationSummary");

    // This is kind of odd logic because we use a REST mutation with a GQL response, but it'll have to do for now.
    return useMutation<IKycContextApplicationFragment, ResponseError, UnitApplicationRequest>(async (request) => {
        const { app_id } = await catchAxiosResponseError<IBlueprintsApplicationResponse, ResponseError>(
            axios.post("accounts/application", request),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (errorResponse, status) => new ResponseError((errorResponse as any).errors.display_error, status),
        );

        const { data } = await urqlClient
            .query<IKycContextQuery, IKycContextQueryVariables>(
                KycContextDocument,
                { applicationInput: { applicationId: app_id } },
                {
                    ...getOperationContext<IKycContextQuery>({
                        additionalTypenameFlags: KYC_CONTEXT_ADDITIONAL_TYPENAME_FLAGS,
                    }),
                    requestPolicy: "network-only",
                },
            )
            .toPromise();

        if (data?.getApplication.application == null) {
            throw new Error(
                compact(["Error loading application", data?.getApplication.errors?.[0].message]).join(": "),
            );
        }

        invalidateApplications();
        return data.getApplication.application;
    });
}

function getBankDetailsErrorMessage(errorResponse: unknown, status: number): ResponseError {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new ResponseError((errorResponse as any)?.errors?.display_error ?? "Error validating bank details", status);
}

export function fetchBankDetails(identifierType: IdentifierType, identifierValue: string): Promise<BankDetails> {
    const searchParams = new URLSearchParams({
        identifier_type: identifierType,
        identifier_value: identifierValue,
    }).toString();
    // auth not required. this endpoint is used for recipient self-onboarding.
    return catchAxiosResponseError(
        axios.get(`accounts/bank_details?${searchParams}`, { headers: NO_AUTH_HEADERS_INIT }),
        getBankDetailsErrorMessage,
    );
}

export function useBankDetailsQuery(
    identifierType: IdentifierType,
    identifierValue: string,
    options?: QueryOptions<BankDetails, ResponseError>,
) {
    return useQuery<BankDetails, ResponseError>(
        AccountsKey.bankDetails(identifierType, identifierValue),
        () => fetchBankDetails(identifierType, identifierValue),
        options,
    );
}

export type GetPromoCodeResponse =
    | {
          valid: true;
          description: string;
          expires: string;
      }
    | {
          valid: false;
      };

export async function fetchPromoCode(promoCode: string): Promise<GetPromoCodeResponse> {
    const searchParams = new URLSearchParams({ promo_code: promoCode });
    return (await axios.get<GetPromoCodeResponse>(`accounts/promo_code?${searchParams}`)).data;
}

export interface IUploadApplicationDocumentRequest {
    application_id: string;
    doc_id: string;
    back?: boolean;
    file: File;
}

export function useUploadApplicationDocument() {
    const { refetchApplication } = useContext(KycContext);

    return useMutation<void, ResponseError, IUploadApplicationDocumentRequest>(
        ({ application_id, doc_id, back, file }) => {
            const searchParams = new URLSearchParams(withoutUndefinedValues({ application_id, doc_id, back }));
            return axios.put(`accounts/upload_doc?${searchParams}`, file, {
                headers: {
                    "Content-Type": file.type,
                },
            });
        },
        {
            onSuccess: () => refetchApplication(),
        },
    );
}

export async function fetchPartnerAccountPrefillData(token: string): Promise<AccountPrefill> {
    const searchParams = new URLSearchParams(withoutUndefinedValues({ token }));
    return (await fetchWithAuth(`accounts/partner_application?${searchParams}`, "json", { skipAuth: true })).data;
}

export function usePartnerAccountPrefillDataQuery(
    token: string,
    options?: QueryOptions<AccountPrefill, ResponseError>,
) {
    return useQuery<AccountPrefill, ResponseError>(
        AccountsKey.accountPrefill(token),
        () => fetchPartnerAccountPrefillData(token),
        options,
    );
}

export interface ISubmitPartnerApplicationRequest {
    application: UnitApplicationRequest;
    token: string;
}

export interface ISubmitPartnerApplicationResponse {
    reset_pw_url: string;
}

export function useSubmitPartnerApplicationMutation() {
    return useMutation<ISubmitPartnerApplicationResponse, ResponseError, ISubmitPartnerApplicationRequest>(
        async ({ application, token }) => {
            const searchParams = new URLSearchParams(withoutUndefinedValues({ token }));
            return await catchAxiosResponseError<ISubmitPartnerApplicationResponse, ResponseError>(
                axios.post(`accounts/partner_application?${searchParams}`, application, {
                    headers: NO_AUTH_HEADERS_INIT,
                }),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (errorResponse, status) => new ResponseError((errorResponse as any).errors.display_error, status),
            );
        },
    );
}
